/* ============================================================
   PRODUCT COLOUR OPTIONS
   One place that answers "what colours does this product come in?"

   Before this, the palette was written out three separate times —
   in Admin.js, in ProductDetail.js and implicitly in ProductCard —
   which meant a colour added in one place did not exist in another.
   Card, detail page and admin now all read from here.
   ============================================================ */

/** The colours an admin can attach photo sets to, picked by hand. */
export const MANUAL_PALETTE = [
  { name: 'Gold', hex: '#D4AF37' },
  { name: 'Rose Gold', hex: '#B76E79' },
  { name: 'Silver', hex: '#C0C0C0' },
  { name: 'Black', hex: '#1a1a1a' },
  { name: 'White', hex: '#F5F5F5' },
  { name: 'Red', hex: '#C0392B' },
  { name: 'Blue', hex: '#2980B9' },
  { name: 'Green', hex: '#27AE60' },
  { name: 'Purple', hex: '#8E44AD' },
  { name: 'Pink', hex: '#E91E8C' },
  { name: 'Pista', hex: '#93C572' },
  { name: 'Multi', hex: 'linear-gradient(135deg,#e91e8c,#f4c430,#2980b9)' },
];

/** name → hex lookup for the manual palette. */
export const PALETTE_HEX = MANUAL_PALETTE.reduce((acc, c) => {
  acc[c.name] = c.hex;
  return acc;
}, {});

const FALLBACK_HEX = '#CCCCCC';

/**
 * Every colour a shopper can choose for this product, in the order
 * they should be shown.
 *
 * Detected colours come first because they carry a real hex read from
 * the photograph. Hand-picked colours follow, then any colour that only
 * exists because it has its own photo set.
 *
 * @param {object} product
 * @returns {Array<{name:string, hex:string, pct?:number, hasPhotos:boolean, detected:boolean}>}
 */
export function colorOptionsFor(product) {
  if (!product) return [];

  const photoSets = product.colorImages || {};
  const out = [];

  const push = (name, hex, extra = {}) => {
    if (!name) return;
    if (out.some((c) => c.name.toLowerCase() === String(name).toLowerCase())) return;
    out.push({
      name,
      hex: hex || PALETTE_HEX[name] || FALLBACK_HEX,
      hasPhotos: Boolean(photoSets[name] && photoSets[name].length),
      detected: false,
      ...extra,
    });
  };

  /* ── Colour variants the admin built ──────────────────────────
     The canonical source: an ordered list of colours, each with its own
     photos and a swatch hex. Every colour here was created by the admin,
     so there are no stray "unusual" colours to filter out. */
  if (Array.isArray(product.colorVariants) && product.colorVariants.length) {
    product.colorVariants.forEach((v) => {
      if (v && v.name) push(v.name, v.hex, { hasPhotos: Boolean(v.images && v.images.length) });
    });
    if (out.length) return out;
  }

  /* ── Older products: colours live only as a photo-set map ─────── */
  const photoSetNames = Object.keys(photoSets).filter(
    (name) => Array.isArray(photoSets[name]) && photoSets[name].length
  );
  if (photoSetNames.length) {
    photoSetNames.forEach((name) => push(name, PALETTE_HEX[name]));
    return out;
  }

  /* ── Single-colour product: the one colour the admin set, or the
     dominant colour read from the photo when nothing was set. ───── */
  const primaryName = (product.colorName || (product.color && product.color.name) || "").trim();
  if (primaryName) {
    const primaryHex =
      (product.color && product.color.hex) ||
      PALETTE_HEX[primaryName] ||
      (product.detectedColors && product.detectedColors[0] && product.detectedColors[0].hex) ||
      FALLBACK_HEX;
    push(primaryName, primaryHex);
    return out;
  }

  if (product.detectedColors && product.detectedColors.length) {
    const top = product.detectedColors[0];
    push(top.name, top.hex, { pct: top.pct, detected: true });
  }

  return out;
}

/**
 * The colour that should go on the order when the shopper has not
 * picked one explicitly.
 */
export function defaultColorFor(product) {
  const options = colorOptionsFor(product);
  return options.length ? options[0] : null;
}

/** True when the shopper genuinely has a choice to make. */
export function hasColorChoice(product) {
  return colorOptionsFor(product).length > 1;
}
